import React, { useEffect } from "react";
import { Row, Col, Form, Input, Card, Button } from "antd";
import "./styles.css";
import { useSelector, useDispatch } from "react-redux";
import { numericInputValidator, numberRegEx } from "../../../Common/Utils";
import {
  setCurrentStep,
  setRideDetails,
} from "../../../../reducers/testRideSlice";
import { TEST_RIDE } from "../../../Common/Constants";
import TimeRangeForm from "./TimeRangeDetails";

const TestRideForm = ({ batteryDetails, timeRangeValidation }) => {
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const testRideFormRef = React.useRef(null);
  const vehicleDetails = useSelector((state) => state.eol.vehicleDetails);
  const dispatch = useDispatch();
  const rideDetails = useSelector((state) => state.testRide?.rideDetails);

  const onFinish = (value) => {
    const { startDate, startTime, endDate, endTime } = value;
    const errorList = timeRangeValidation(startDate, startTime, endDate, endTime)
    if(errorList){
      testRideFormRef?.current?.setFields(errorList);
    } else {
      dispatch(
        setRideDetails({
          ...rideDetails,
          ...value,
        })
      );
      dispatch(setCurrentStep(1));
    }
  };

  const totalWeightUpdate = () => {
    const riderWeight = testRideFormRef.current?.getFieldValue("riderWeight")
      ? Number(testRideFormRef.current?.getFieldValue("riderWeight"))
      : 0;
    const extraWeight = testRideFormRef.current?.getFieldValue(
      "pillionRiderWeight"
    )
      ? Number(testRideFormRef.current?.getFieldValue("pillionRiderWeight"))
      : 0;
    testRideFormRef.current?.setFieldsValue({
      totalWeight: vehicleDetails?.weight + riderWeight + extraWeight,
    });
  };

  useEffect(() => {
    testRideFormRef.current?.setFieldsValue({
      startDate: null,
      startTime: null,
      endDate: null,
      endTime: null,
      riderWeight: null,
      pillionRiderName: null,
      pillionRiderWeight: null,
      totalWeight: null,
      batteryManufacturer: batteryDetails?.manufacturer,
      batteryCapacity: batteryDetails?.capacity,
      batteryModel: batteryDetails?.model,
    });
  }, [batteryDetails]);

  return (
    <Form
      name="testRideForm"
      layout="vertical"
      ref={testRideFormRef}
      onFinish={onFinish}
      initialValues={{
        ...rideDetails,
        riderName:
          rideDetails.riderName ||
          userDetails?.firstName + " " + userDetails?.lastName,
        totalWeight: rideDetails.totalWeight || vehicleDetails?.weight,
      }}
      validateMessages={{
        required: TEST_RIDE.FORM.REQUIRED_FIELDS.BLANK_ERROR,
        whitespace: TEST_RIDE.FORM.REQUIRED_FIELDS.BLANK_ERROR,
      }}
      autoComplete="off"
    >
      <Row gutter={16}>
        <TimeRangeForm formRef={testRideFormRef} />
        <Col xs={24} sm={24} md={24} lg={16} xl={16} xxl={18}>
          <Card bordered={false} className="input-box">
            <Row gutter={16}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="riderName"
                  label={TEST_RIDE.FORM.REQUIRED_FIELDS.RIDER_NAME}
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                    },
                  ]}
                >
                  <Input
                    title="riderName"
                    placeholder="Rider Name"
                    maxLength={50}
                    size="large"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="riderWeight"
                  label={TEST_RIDE.FORM.REQUIRED_FIELDS.RIDER_WEIGHT}
                  rules={[
                    {
                      required: true,
                    },
                    {
                      message: "Enter a valid number",
                      validator: (_, value) => numericInputValidator(value),
                    },
                  ]}
                >
                  <Input
                    placeholder="Rider weight"
                    maxLength={3}
                    name="riderWeight"
                    title="riderWeight"
                    onChange={totalWeightUpdate}
                    allowClear
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="pillionRiderName"
                  label={TEST_RIDE.FORM.REQUIRED_FIELDS.PILLION_RIDER_NAME}
                >
                  <Input
                    size="large"
                    maxLength={50}
                    title="pillionRiderName"
                    placeholder="Pillion Rider Name"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="pillionRiderWeight"
                  label={TEST_RIDE.FORM.REQUIRED_FIELDS.PILLION_RIDER_WEIGHT}
                  rules={[
                    {
                      message: "Enter a valid number",
                      validator: (_, value) => {
                        if (
                          value &&
                          (!numberRegEx.test(value) || parseInt(value) > 150)
                        ) {
                          return Promise.reject();
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="Pillion Rider weight"
                    maxLength={3}
                    name="pillionRiderWeight"
                    title="pillionRiderWeight"
                    onChange={totalWeightUpdate}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  className="mb0"
                  name="totalWeight"
                  label={TEST_RIDE.FORM.REQUIRED_FIELDS.TOTAL_WEIGHT}
                >
                  <Input
                    size="large"
                    title="totalWeight"
                    type="number"
                    readOnly
                    disabled
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16} className="mt1">
              <Col span={24}>
                <Form.Item
                  label={
                    <span className="bold">
                      {TEST_RIDE.FORM.REQUIRED_FIELDS.BATTERY_DETAILS}
                    </span>
                  }
                  className="mb0"
                >
                  <Row gutter={16}>
                    <Col xs={24} sm={12} md={12}>
                      <Form.Item
                        name="batteryManufacturer"
                        label={TEST_RIDE.FORM.REQUIRED_FIELDS.MANUFACTURER}
                      >
                        <Input
                          size="large"
                          placeholder="Manufacturer"
                          maxLength={50}
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                      <Form.Item
                        name="batteryCapacity"
                        label={TEST_RIDE.FORM.REQUIRED_FIELDS.CAPACITY}
                        rules={[
                          {
                            message: "Enter a valid number",
                            validator: (_, value) =>
                              numericInputValidator(value, "decimalNegative"),
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          placeholder="Capacity"
                          maxLength={6}
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                      <Form.Item
                        className="mb0"
                        name="batteryModel"
                        label={TEST_RIDE.FORM.REQUIRED_FIELDS.MODEL}
                      >
                        <Input
                          size="large"
                          placeholder="Model"
                          maxLength={50}
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Col span={24}>
        <Form.Item
          className="mt2"
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Row>
            <Button
              data-testid="submitBtn"
              type="primary"
              className="submit-btn"
              htmlType="submit"
              title="riderNextBtn"
              style={{ width: "80px", marginLeft: "2rem" }}
            >
              Next
            </Button>
          </Row>
        </Form.Item>
      </Col>
    </Form>
  );
};

export default TestRideForm;
